/* ══════════════════════════ talking to the page ══════════════════════════

   The handful of things every part of the interface needs: find an element,
   build one, show a message, open and close a modal, ask a question, read a
   custom property back out of the stylesheet.

   `askText` and `askConfirm` exist because `window.prompt` and
   `window.confirm` block the whole page, which breaks automation and makes the
   app untestable at exactly the moments that matter most — naming a course,
   and replacing a term with a backup.

   This is the floor of the ui layer, in the same way `core/time.js` is the
   floor of everything. It may reach for the DOM, which is why it is not in
   core, and it may not know anything about studyHub. */

/** Find one of our own elements by id.
 *
 *  Typed as returning `any` rather than `HTMLElement | null`, deliberately.
 *  Every id this is called with is in our own index.html; a missing one is a
 *  bug that announces itself on the first render, not a case to be handled.
 *  Without this the checker asks for a null guard and an element cast on every
 *  single line of every renderer — around ninety of them — which is how a
 *  checker gets switched off. The nullability that matters here is in the
 *  data, and that is still checked.
 *  @returns {any} */
export const $ = (id) => document.getElementById(id);
export const el = (tag, cls, text) => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
};

/* ───────────────────────────── state ───────────────────────────── */

/* Saturated inks — these show up as 3px spine strips and as tinted event chips
   on cream paper, so they need to be darker than the old on-dark palette. */

export function toast(msg, bad = false) {
  const t = el('div', 'toast' + (bad ? ' is-bad' : ''), msg);
  $('toasts').appendChild(t);
  setTimeout(() => { t.style.opacity = '0'; t.style.transition = 'opacity .3s'; }, 2600);
  setTimeout(() => t.remove(), 3000);
}

export let openModals = [];
export function openModal(id) {
  $('overlay').hidden = false;
  $(id).hidden = false;
  openModals.push(id);
}
export function closeModal(id) {
  $(id).hidden = true;
  openModals = openModals.filter((m) => m !== id);
  if (!openModals.length) $('overlay').hidden = true;
}

/** The three right-hand drawers share one slot, so opening any closes the others. */
export function setDrawer(which) {
  document.body.classList.toggle('panel-open', which === 'form');
  document.body.classList.toggle('tasks-open', which === 'tasks');
  document.body.classList.toggle('settings-open', which === 'settings');
  document.body.classList.toggle('series-open', which === 'series');
}

/** Ask for a single line of text. Replaces window.prompt (blocking dialogs break automation). */
export function askText(title, label, initial, onOk) {
  $('promptTitle').textContent = title;
  $('promptLabel').textContent = label;
  const input = $('promptInput');
  input.value = initial || '';
  openModal('promptModal');
  setTimeout(() => { input.focus(); input.select(); }, 30);

  const ok = () => {
    const v = input.value.trim();
    cleanup();
    closeModal('promptModal');
    if (v) onOk(v);
  };
  const key = (e) => { if (e.key === 'Enter') { e.preventDefault(); ok(); } };
  const cancel = () => cleanup();
  function cleanup() {
    $('promptOk').removeEventListener('click', ok);
    input.removeEventListener('keydown', key);
    $('promptModal').querySelector('[data-close]').removeEventListener('click', cancel);
  }
  $('promptOk').addEventListener('click', ok);
  input.addEventListener('keydown', key);
  $('promptModal').querySelector('[data-close]').addEventListener('click', cancel);
}

/** Ask yes or no. Same reasoning as askText: window.confirm blocks. */
export function askConfirm(title, body, okLabel, onOk) {
  $('confirmTitle').textContent = title;
  $('confirmBody').textContent = body;
  const okBtn = $('confirmOk');
  okBtn.textContent = okLabel || 'Yes';
  openModal('confirmModal');

  const cancelBtn = $('confirmModal').querySelector('[data-close]');
  const ok = () => { cleanup(); closeModal('confirmModal'); onOk(); };
  const cancel = () => cleanup();
  function cleanup() {
    okBtn.removeEventListener('click', ok);
    cancelBtn.removeEventListener('click', cancel);
  }
  okBtn.addEventListener('click', ok);
  cancelBtn.addEventListener('click', cancel);
}

/** The computed value of a custom property on <html>, trimmed. */
export const cssVar = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/** Same, forced to a hex string an <input type=color> will accept. */
export function cssVarHex(name) {
  const v = cssVar(name);
  if (/^#[0-9a-f]{6}$/i.test(v)) return v;
  // resolve keywords, rgb() and var() chains by letting the engine paint it
  const probe = el('span');
  probe.style.color = v || '#000000';
  document.body.appendChild(probe);
  const m = getComputedStyle(probe).color.match(/\d+/g);
  probe.remove();
  if (!m) return '#000000';
  return '#' + m.slice(0, 3).map((n) => (+n).toString(16).padStart(2, '0')).join('');
}

/** Whether the machine has asked for less animation. Every transition and
 *  confetti burst in the app is gated on this. */
export const reducedMotion = () =>
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
